// Story 5.3 — TanStack Query read hook for the sealed-dossier
// floor-price autopop.
//
// GET /api/v1/marketplace/floor/sealed-dossier?drop_id={}
//
// Sibling to Story 5.2's useShareTierFloorPrice. Sealed dossiers from
// the same drop are fungible (identical art; only edition_number
// differs) so the natural grouping key is drop_id — not creator+tier.
//
// Consumed by ListSealedDossierForSaleDialog on dialog open. `enabled`
// is wired to dialog-open state so the request only fires when the
// dialog actually mounts.
//
// Returns `{ floor_price_cents: number | null }`. `null` means "no
// active listings exist for this drop yet" — the dialog leaves the
// price input empty and lets the user pick.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface SealedDossierDropFloorPriceResponse {
  floor_price_cents: number | null;
}

export interface UseSealedDossierDropFloorPriceArgs {
  dropId: string;
  enabled: boolean;
}

async function fetchSealedDossierDropFloorPrice(
  dropId: string,
): Promise<SealedDossierDropFloorPriceResponse> {
  const url = `/api/v1/marketplace/floor/sealed-dossier?drop_id=${encodeURIComponent(
    dropId,
  )}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Floor-price lookup failed (HTTP ${response.status})`);
  }
  const body = (await response.json()) as {
    data: SealedDossierDropFloorPriceResponse;
  };
  return body.data;
}

export function useSealedDossierDropFloorPrice(
  args: UseSealedDossierDropFloorPriceArgs,
): UseQueryResult<SealedDossierDropFloorPriceResponse, Error> {
  return useQuery<SealedDossierDropFloorPriceResponse, Error>({
    queryKey: ["marketplace-floor-sealed-dossier", args.dropId],
    queryFn: () => fetchSealedDossierDropFloorPrice(args.dropId),
    enabled: args.enabled,
    staleTime: 5 * 60 * 1000,
  });
}
